import { useEffect, useState } from 'react';

export interface Expansion {
    id: string | null;
    renderedId: string | null;
    isExpanded: (id: string) => boolean;
    isRendered: (id: string) => boolean;
    onCollapsed: (id: string) => void;
    toggle: (id: string) => void;
    collapse: () => void;
}

export interface UseExpansionArgs {
    ids: string[];
    onExpand?: () => void;
}

export function useExpansion(args: UseExpansionArgs): Expansion {
    const { ids, onExpand } = args;
    const [id, setId] = useState<string | null>(null);
    const [renderedId, setRenderedId] = useState<string | null>(null);

    useEffect(() => {
        if (id !== null && !ids.includes(id)) {
            setId(null);
        }
        if (renderedId !== null && !ids.includes(renderedId)) {
            setRenderedId(null);
        }
        // eslint-disable-next-line react/exhaustive-deps -- react only on `ids` identity, not on `id`/`renderedId`
    }, [ids]);

    const isExpanded = (candidate: string): boolean => candidate === id;

    const isRendered = (candidate: string): boolean => candidate === renderedId;

    const toggle = (candidate: string): void => {
        if (candidate === id) {
            setId(null);
            return;
        }
        setId(candidate);
        setRenderedId(candidate);
        onExpand?.();
    };

    const collapse = (): void => {
        setId(null);
    };

    const onCollapsed = (candidate: string): void => {
        setRenderedId((current) => (current === candidate ? null : current));
    };

    return {
        id,
        renderedId,
        isExpanded,
        isRendered,
        onCollapsed,
        toggle,
        collapse,
    };
}
